import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { useAuth } from '../context/AuthContext';
import { getCurrentEvent, getAllEvents, getEvent, getFights, getMyPicks, getLeaguePicks, getMyLeagues } from '../services/api';
import Loading from '../components/Loading';
import '../styles/app/locksheet.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const proxyImage = (url) => {
  if (!url) return url;
  if (url.startsWith('/')) return url; // local public file — no proxy needed
  return `${API_BASE}/image-proxy?url=${encodeURIComponent(url)}`;
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const getLastName = (name) => {
  if (!name) return '';
  return name.split(' ').pop().toUpperCase();
};

function LockSheet() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const eventIdParam = searchParams.get('event');
  const poolIdParam = searchParams.get('pool');
  const userIdParam = searchParams.get('user');
  const memeMode = searchParams.get('meme') === 'true';
  const cardRef = useRef(null);
  const previewWrapRef = useRef(null);
  const preparedExportRef = useRef(null);
  const preparingExportRef = useRef(false);

  const [event, setEvent] = useState(null);
  const [fights, setFights] = useState([]);
  const [picks, setPicks] = useState({});
  const [viewingUsername, setViewingUsername] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [exportPreparing, setExportPreparing] = useState(false);
  const [events, setEvents] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(eventIdParam || '');
  const [selectedPoolId, setSelectedPoolId] = useState(poolIdParam || '');

  useEffect(() => {
    if (memeMode) {
      setEvent({ id: 999, name: 'UFC FIGHT NIGHT: MONKEY VS. ASPINALL', date: new Date().toISOString(), status: 'open' });
      setFights([{
        id: 1, fight_order: 0, card_type: 'main',
        fighter1_name: 'The Monkey Punch', fighter1_image_url: '/punch-the-monkey.png',
        fighter2_name: 'Tom Aspinall', fighter2_image_url: 'https://www.sherdog.com/image_crop/200/300/_images/fighter/20220401020925_Tom_Aspinall_ff.JPG',
        weight_class: 'Heavyweight', winner: null, is_cancelled: false
      }]);
      setPicks({ 1: { winner: 'The Monkey Punch', method: 'KO/TKO' } });
      setViewingUsername('kyledboi');
      setLoading(false);
      return;
    }
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (memeMode) return;
    if (selectedEventId && selectedPoolId) {
      loadPicksData(selectedEventId, selectedPoolId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEventId, selectedPoolId, userIdParam]);

  const getExportKey = () => {
    const picksCount = Object.keys(picks).length;
    return [
      selectedEventId || '',
      selectedPoolId || '',
      userIdParam || 'self',
      event?.id || '',
      viewingUsername || '',
      fights.length,
      picksCount
    ].join('|');
  };

  const isIOS = () => {
    const ua = navigator.userAgent || '';
    const iOSDevice = /iPad|iPhone|iPod/.test(ua);
    const iPadOSDesktopUA = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    return iOSDevice || iPadOSDesktopUA;
  };

  const waitForImageReady = async (img) => {
    if (!img) return;
    if (!img.complete) {
      await new Promise(resolve => {
        const timeout = setTimeout(() => {
          img.removeEventListener('load', done);
          img.removeEventListener('error', done);
          resolve();
        }, 4000);
        const done = () => {
          clearTimeout(timeout);
          img.removeEventListener('load', done);
          img.removeEventListener('error', done);
          resolve();
        };
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      });
    }
    if (img.complete && img.naturalWidth === 0) return;
    if (img.decode) {
      try {
        await img.decode();
      } catch {
        // Ignore decode errors and allow fallback rendering.
      }
    }
  };

  const fileNameForExport = `${event?.name || 'picks'}-picks.png`;

  const buildExportBlob = async (exportKey) => {
    const sourceEl = cardRef.current;
    if (!sourceEl) return null;

    const cloneWrap = document.createElement('div');
    cloneWrap.style.position = 'fixed';
    cloneWrap.style.left = '-10000px';
    cloneWrap.style.top = '0';
    cloneWrap.style.zIndex = '-1';
    cloneWrap.style.pointerEvents = 'none';

    const cloneEl = sourceEl.cloneNode(true);
    const igWidth = 1080;
    const igHeight = 1350;
    // Narrow capture = higher pixelScale = everything renders larger in the 1080px output.
    const captureWidth = 480;

    cloneEl.style.width = `${captureWidth}px`;
    cloneEl.style.maxWidth = `${captureWidth}px`;
    cloneWrap.appendChild(cloneEl);
    document.body.appendChild(cloneWrap);

    try {
      const imgs = cloneEl.querySelectorAll('img');
      await Promise.all([...imgs].map(async (img) => {
        if (!img.src || img.src.startsWith('data:')) {
          await waitForImageReady(img);
        } else {
          try {
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 7000);
            let resp;
            try {
              resp = await fetch(img.src, { signal: controller.signal });
            } finally {
              clearTimeout(timeout);
            }
            if (!resp.ok) throw new Error(`Bad image response: ${resp.status}`);
            const blob = await resp.blob();
            const dataUrl = await new Promise(resolve => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.readAsDataURL(blob);
            });
            img.src = dataUrl;
          } catch {
            img.style.display = 'none';
          }
          await waitForImageReady(img);
        }

        // html2canvas can distort object-fit on iOS; render fighter portraits as backgrounds.
        const headshot = img.closest('.pp-headshot');
        if (headshot && img.style.display !== 'none' && img.src) {
          const safeSrc = img.src.replace(/"/g, '\\"');
          headshot.style.backgroundImage = `url("${safeSrc}")`;
          headshot.style.backgroundSize = 'cover';
          headshot.style.backgroundPosition = 'top center';
          headshot.style.backgroundRepeat = 'no-repeat';
          img.style.display = 'none';
          const initials = headshot.querySelector('.pp-initials');
          if (initials) {
            initials.style.display = 'none';
          }
        }
      }));

      // Replace logo background-div with an <img> tag — html2canvas renders
      // img elements at full resolution, unlike CSS backgrounds which get
      // rasterized at the element's CSS pixel size.
      const logoEl = cloneEl.querySelector('.pp-logo');
      if (logoEl) {
        try {
          const resp = await fetch('/logo-cropped.png');
          const logoBlob = await resp.blob();
          const dataUrl = await new Promise(resolve => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(logoBlob);
          });
          const logoImg = document.createElement('img');
          logoImg.src = dataUrl;
          logoImg.style.width = '48px';
          logoImg.style.height = 'auto';
          logoImg.style.minWidth = '48px';
          logoImg.style.filter = 'drop-shadow(0 0 6px rgba(227, 55, 47, 0.4))';
          logoEl.replaceWith(logoImg);
        } catch {
          // Leave CSS background as-is; html2canvas may still render it.
        }
      }

      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

      const cardW = cloneEl.offsetWidth;
      const cardH = cloneEl.offsetHeight;
      if (!cardW || !cardH) return null;

      const pixelScale = igWidth / cardW;

      const canvas = await html2canvas(cloneEl, {
        backgroundColor: '#060912',
        scale: pixelScale,
        useCORS: true,
        allowTaint: false,
        logging: false,
        width: cardW,
        height: cardH,
      });

      const igCanvas = document.createElement('canvas');
      igCanvas.width = igWidth;
      igCanvas.height = igHeight;
      const ctx = igCanvas.getContext('2d');
      ctx.fillStyle = '#060912';
      ctx.fillRect(0, 0, igWidth, igHeight);

      // Fit entire card in the frame — scale down if taller than 1350, scale up if shorter.
      const scale = Math.min(igWidth / canvas.width, igHeight / canvas.height);
      const drawW = canvas.width * scale;
      const drawH = canvas.height * scale;
      const drawX = (igWidth - drawW) / 2;
      const drawY = 0;
      ctx.drawImage(canvas, drawX, drawY, drawW, drawH);

      const blob = await new Promise((resolve, reject) => {
        igCanvas.toBlob((b) => {
          if (b) resolve(b);
          else reject(new Error('Failed to export blob'));
        }, 'image/png');
      });

      return { blob, fileName: fileNameForExport, key: exportKey };
    } finally {
      cloneWrap.remove();
    }
  };

  useEffect(() => {
    if (!event || !cardRef.current) return;
    if (!navigator.share || !isIOS()) return;

    const exportKey = getExportKey();
    if (preparedExportRef.current?.key === exportKey || preparingExportRef.current) return;

    let cancelled = false;
    const prepTimer = setTimeout(async () => {
      if (cancelled) return;
      preparingExportRef.current = true;
      setExportPreparing(true);
      try {
        const payload = await buildExportBlob(exportKey);
        if (!cancelled && payload?.key === exportKey) {
          preparedExportRef.current = payload;
        }
      } catch {
        // Silent: this is best-effort prebuild for iOS share reliability.
      } finally {
        preparingExportRef.current = false;
        setExportPreparing(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(prepTimer);
      preparingExportRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, fights, picks, viewingUsername, selectedEventId, selectedPoolId, userIdParam]);

  useEffect(() => {
    if (!navigator.share || !isIOS()) return;
    preparedExportRef.current = null;
  }, [event, fights, picks, viewingUsername, selectedEventId, selectedPoolId, userIdParam]);


  const loadInitialData = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [eventsRes, leaguesRes] = await Promise.all([
        getAllEvents(),
        getMyLeagues()
      ]);

      const allEvents = eventsRes.data || [];
      const sortedEvents = allEvents
        .filter(e => e.status === 'open' || e.status === 'locked' || e.status === 'completed')
        .sort((a, b) => new Date(b.date) - new Date(a.date));
      setEvents(sortedEvents);
      setLeagues(leaguesRes.data || []);

      let autoEventId = eventIdParam;
      if (!autoEventId && sortedEvents.length > 0) {
        const currentRes = await getCurrentEvent().catch(() => null);
        autoEventId = currentRes?.data?.id?.toString() || sortedEvents[0].id.toString();
      }
      if (autoEventId) setSelectedEventId(autoEventId);

      if (poolIdParam) {
        setSelectedPoolId(poolIdParam);
      } else if (leaguesRes.data?.length > 0) {
        setSelectedPoolId(leaguesRes.data[0].id.toString());
      }
    } catch (err) {
      setLoadError(err.response?.data?.error || 'Couldn\'t load your events and pools. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadPicksData = async (eventId, poolId) => {
    setLoadError('');
    try {
      const [eventRes, fightsRes] = await Promise.all([
        getEvent(eventId),
        getFights(eventId)
      ]);
      setEvent(eventRes.data);
      setFights(fightsRes.data || []);

      const isLockedOrCompleted = eventRes.data?.status === 'locked' || eventRes.data?.status === 'completed';

      if (userIdParam && isLockedOrCompleted) {
        const allPicksRes = await getLeaguePicks(poolId, eventId).catch(() => ({ data: { users: [] } }));
        const targetUser = (allPicksRes.data.users || []).find(u => u.id === parseInt(userIdParam));
        if (targetUser) {
          setViewingUsername(targetUser.username);
          const picksMap = {};
          (targetUser.picks || []).forEach(pick => {
            picksMap[pick.fight_id] = { winner: pick.picked_fighter, method: pick.picked_method };
          });
          setPicks(picksMap);
        }
      } else {
        setViewingUsername(user?.username || 'Anonymous');
        const picksRes = await getMyPicks(poolId, eventId).catch(() => ({ data: [] }));
        const picksMap = {};
        (picksRes.data || []).forEach(pick => {
          picksMap[pick.fight_id] = { winner: pick.picked_fighter, method: pick.picked_method };
        });
        setPicks(picksMap);
      }
    } catch (err) {
      setLoadError(err.response?.data?.error || 'Couldn\'t load this Lock Sheet. Try a different event or pool, or check your connection.');
    }
  };

  const saveAsImage = async () => {
    if (!cardRef.current || saving) return;
    setSaving(true);
    try {
      const exportKey = getExportKey();
      let payload = preparedExportRef.current?.key === exportKey
        ? preparedExportRef.current
        : null;
      if (!payload) {
        payload = await buildExportBlob(exportKey);
      }
      if (!payload?.blob) {
        throw new Error('No image data generated');
      }

      preparedExportRef.current = payload;
      const { blob, fileName } = payload;
      const file = new File([blob], fileName, { type: 'image/png' });

      // iOS: always use share sheet (single tap opens it)
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file] });
      } else {
        // Desktop: download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = fileName;
        link.href = url;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Failed to save image:', err);
        alert('Failed to save image. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  // Scale card to fit viewport on mobile
  const [previewScale, setPreviewScale] = useState(1);
  const [previewHeight, setPreviewHeight] = useState(0);
  useEffect(() => {
    const updateScale = () => {
      const containerWidth = Math.min(window.innerWidth - 32, 480);
      setPreviewScale(containerWidth / 480);
    };
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, []);

  // Measure actual card height after render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (cardRef.current) {
      setPreviewHeight(cardRef.current.offsetHeight);
    }
  }, [event, fights, picks, previewScale]);

  if (loading) return <Loading />;

  if (loadError) {
    return (
      <div className="container ls-page">
        <div className="pnp-empty ls-error">
          <div className="pnp-empty-icon" aria-hidden="true">!</div>
          <h3 className="pnp-empty-title">Lock Sheet unavailable</h3>
          <p>{loadError}</p>
          <div className="pnp-empty-actions">
            <button
              onClick={() => loadInitialData()}
              className="btn btn-primary"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Sort ascending — main event (fight_order 0) first
  const allFights = fights
    .filter(f => !f.is_cancelled)
    .sort((a, b) => (a.fight_order ?? 0) - (b.fight_order ?? 0));
  const mainCardFights = allFights.filter(f => f.card_type === 'main');
  const prelimFights = allFights.filter(f => f.card_type === 'prelim');
  const earlyFights = allFights.filter(f => f.card_type === 'early');

  const isCompleted = event?.status === 'completed';

  const totalPicks = allFights.filter(f => picks[f.id]?.winner && f.winner !== 'Draw').length;
  const correctPicks = allFights.filter(f => f.winner && f.winner !== 'Draw' && picks[f.id]?.winner === f.winner).length;
  const wrongPicks = allFights.filter(f => f.winner && f.winner !== 'Draw' && picks[f.id]?.winner && picks[f.id]?.winner !== f.winner).length;

  const handleImageLoad = (e) => {
    const initialsEl = e.target.previousSibling;
    if (initialsEl) initialsEl.style.display = 'none';
  };

  const renderHeadshot = (fight, fighterName, imageUrl, isPicked) => {
    const hasResult = !!fight.winner;
    const isDraw = fight.winner === 'Draw';
    const isCorrect = isPicked && hasResult && !isDraw && fighterName === fight.winner;
    const isWrong = isPicked && hasResult && !isDraw && fighterName !== fight.winner;

    let ringClass = '';
    if (isDraw && isPicked) ringClass = 'pp-ring-picked';
    else if (isCorrect) ringClass = 'pp-ring-correct';
    else if (isWrong) ringClass = 'pp-ring-wrong';
    else if (isPicked) ringClass = 'pp-ring-picked';

    const showCheck = isPicked && !hasResult;
    const showCorrect = isCorrect;
    const showWrong = isWrong;

    return (
      <div className={`pp-headshot ${ringClass}`}>
        <span className="pp-initials">{getInitials(fighterName)}</span>
        {imageUrl && (
          <img
            src={proxyImage(imageUrl)}
            alt={fighterName}
            crossOrigin="anonymous"
            onLoad={handleImageLoad}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
        {showCheck && (
          <div className="pp-check">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
        )}
        {showCorrect && (
          <div className="pp-check pp-check-correct">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
        )}
        {showWrong && (
          <div className="pp-check pp-check-wrong">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </div>
        )}
      </div>
    );
  };

  // Main event — big hero layout (first fight only)
  const renderMainEvent = (fight) => {
    const pick = picks[fight.id];
    const isPicked1 = pick?.winner === fight.fighter1_name;
    const isPicked2 = pick?.winner === fight.fighter2_name;
    const isDraw = fight.winner === 'Draw';

    return (
      <div key={fight.id} className={`pp-main-event ${isDraw ? 'pp-draw' : ''}`}>
        <div className="pp-main-fighters">
          {renderHeadshot(fight, fight.fighter1_name, fight.fighter1_image_url, isPicked1)}
          {renderHeadshot(fight, fight.fighter2_name, fight.fighter2_image_url, isPicked2)}
        </div>
        <div className="pp-main-names">
          <span className={isPicked1 ? 'pp-name-on' : 'pp-name-off'}>{getLastName(fight.fighter1_name)}</span>
          <span className="pp-vs">vs</span>
          <span className={isPicked2 ? 'pp-name-on' : 'pp-name-off'}>{getLastName(fight.fighter2_name)}</span>
        </div>
        <div className="pp-division">{fight.weight_class}</div>
      </div>
    );
  };

  // Small bout — used in 4-per-row grid
  const renderSmallBout = (fight) => {
    const pick = picks[fight.id];
    const isPicked1 = pick?.winner === fight.fighter1_name;
    const isPicked2 = pick?.winner === fight.fighter2_name;
    const isDraw = fight.winner === 'Draw';

    return (
      <div key={fight.id} className={`pp-bout ${isDraw ? 'pp-draw' : ''}`}>
        <div className="pp-bout-fighters">
          {renderHeadshot(fight, fight.fighter1_name, fight.fighter1_image_url, isPicked1)}
          {renderHeadshot(fight, fight.fighter2_name, fight.fighter2_image_url, isPicked2)}
        </div>
        <div className="pp-bout-names">
          <span className={isPicked1 ? 'pp-name-on' : 'pp-name-off'}>{getLastName(fight.fighter1_name)}</span>
          <span className="pp-vs-sm">vs</span>
          <span className={isPicked2 ? 'pp-name-on' : 'pp-name-off'}>{getLastName(fight.fighter2_name)}</span>
        </div>
        <div className="pp-division-sm">{fight.weight_class}</div>
      </div>
    );
  };

  // Co-main — medium hero (used for 2nd fight on main card)
  const renderCoMain = (fight) => {
    const pick = picks[fight.id];
    const isPicked1 = pick?.winner === fight.fighter1_name;
    const isPicked2 = pick?.winner === fight.fighter2_name;
    const isDraw = fight.winner === 'Draw';

    return (
      <div key={fight.id} className={`pp-co-main ${isDraw ? 'pp-draw' : ''}`}>
        <div className="pp-co-main-fighters">
          {renderHeadshot(fight, fight.fighter1_name, fight.fighter1_image_url, isPicked1)}
          {renderHeadshot(fight, fight.fighter2_name, fight.fighter2_image_url, isPicked2)}
        </div>
        <div className="pp-co-main-names">
          <span className={isPicked1 ? 'pp-name-on' : 'pp-name-off'}>{getLastName(fight.fighter1_name)}</span>
          <span className="pp-vs-co">vs</span>
          <span className={isPicked2 ? 'pp-name-on' : 'pp-name-off'}>{getLastName(fight.fighter2_name)}</span>
        </div>
        <div className="pp-division-sm">{fight.weight_class}</div>
      </div>
    );
  };

  const renderSection = (label, sectionFights) => {
    if (sectionFights.length === 0) return null;

    const isMainCard = label === 'MAIN CARD';
    // Main card: first fight = hero, second fight = co-main, rest = grid
    const heroFight = isMainCard ? sectionFights[0] : null;
    const coMainFight = isMainCard && sectionFights.length > 1 ? sectionFights[1] : null;
    const gridFights = isMainCard ? sectionFights.slice(coMainFight ? 2 : 1) : sectionFights;

    // Pack grid fights into rows of 4 (like the UFC flyer)
    const rows = [];
    for (let i = 0; i < gridFights.length; i += 4) {
      rows.push(gridFights.slice(i, i + 4));
    }

    return (
      <div className="pp-section">
        <div className="pp-section-bar">{label}</div>
        {heroFight && (
          <div className="pp-heroes">
            {renderMainEvent(heroFight)}
            {coMainFight && renderCoMain(coMainFight)}
          </div>
        )}
        {rows.map((row, rowIdx) => (
          <div key={rowIdx} className="pp-grid-row">
            {row.map(f => renderSmallBout(f))}
          </div>
        ))}
      </div>
    );
  };

  const cardContent = (
    <div ref={cardRef} className="pp-card">
      <div className="pp-header">
        <div className="pp-logo" role="img" aria-label="PNP" />
        <div className="pp-header-info">
          <div className="pp-event-name">{event?.name}</div>
          <div className="pp-event-date">
            {event && new Date(event.date).toLocaleDateString('en-US', {
              weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
            }).toUpperCase()}
          </div>
          <div className="pp-event-site">picknpray.com</div>
        </div>
      </div>

      <div className="pp-user-bar">@{viewingUsername}'s Picks</div>

      {renderSection('MAIN CARD', mainCardFights)}
      {renderSection('PRELIMS', prelimFights)}
      {renderSection('EARLY PRELIMS', earlyFights)}

      {totalPicks > 0 && (
        <div className="pp-score-bar">
          {isCompleted || correctPicks + wrongPicks > 0 ? (
            <>
              <span className="pp-score-correct">{correctPicks} Correct</span>
              <span className="pp-score-sep">|</span>
              <span className="pp-score-wrong">{wrongPicks} Wrong</span>
              <span className="pp-score-sep">|</span>
              <span className="pp-score-total">{totalPicks} Picks</span>
            </>
          ) : (
            <span className="pp-score-total">{totalPicks} Picks Locked In</span>
          )}
        </div>
      )}

      <div className="pp-watermark">@picknpray_mma</div>

    </div>
  );

  return (
    <div className="container ls-page">
      {!memeMode && (
        <Link to="/dashboard" className="ls-back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Back to Dashboard
        </Link>
      )}

      <div className={`ls-layout${event ? '' : ' is-solo'}`}>
        <div className="ls-side">
          {!memeMode && (
            <>
              <header className="ls-head pnp-rise">
                <p className="pnp-eyebrow">Share your card</p>
                <h1 className="ls-title">Lock <em>Sheet</em></h1>
                <p className="ls-sub">Your picks as a fight poster. Pick an event and pool, then save it to post.</p>
              </header>

              <div className="ls-controls pnp-rise" style={{ '--i': 1 }}>
                <div className="form-group">
                  <label className="form-label" htmlFor="ls-event">Event</label>
                  <select
                    id="ls-event"
                    value={selectedEventId}
                    onChange={(e) => setSelectedEventId(e.target.value)}
                    className="form-select preview-select"
                  >
                    <option value="">Select Event</option>
                    {events.map(e => (
                      <option key={e.id} value={e.id}>
                        {e.name} {e.status === 'completed' ? '(Done)' : e.status === 'locked' ? '(Locked)' : '(Open)'}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="ls-pool">Pool</label>
                  <select
                    id="ls-pool"
                    value={selectedPoolId}
                    onChange={(e) => setSelectedPoolId(e.target.value)}
                    className="form-select preview-select"
                  >
                    <option value="">Select Pool</option>
                    {leagues.map(l => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}

          {event && (
            <div className="ls-actions">
              <button
                className="btn btn-primary btn-lg btn-block"
                onClick={saveAsImage}
                disabled={saving}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                {saving
                  ? 'Saving...'
                  : (navigator.share && isIOS() && exportPreparing)
                    ? 'Preparing Image...'
                    : 'Save as Image'}
              </button>
              <p className="ls-hint">
                Save to camera roll and share on Instagram
              </p>
            </div>
          )}

          {!event && selectedEventId && (
            <div className="ls-status ls-skel" aria-busy="true">
              <div className="pnp-skel" style={{ height: 76 }} />
              <div className="pnp-skel" style={{ height: 30 }} />
              <div className="pnp-skel" style={{ height: 180 }} />
              <div className="pnp-skel" style={{ height: 120 }} />
              <p className="pnp-loading-note">Loading event data...</p>
            </div>
          )}
          {!selectedEventId && (
            <div className="ls-status pnp-empty">
              <div className="pnp-empty-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="3" width="16" height="18" rx="2" />
                  <line x1="8" y1="8" x2="16" y2="8" />
                  <line x1="8" y1="12" x2="16" y2="12" />
                  <line x1="8" y1="16" x2="12" y2="16" />
                </svg>
              </div>
              <p>Select an event and pool to view your Lock Sheet.</p>
            </div>
          )}
        </div>

        {event && (
          <div className="ls-stage pnp-rise" style={{ '--i': 2 }}>
            <div ref={previewWrapRef} className="ls-frame" style={{
              width: `${480 * previewScale}px`,
              height: previewHeight ? `${previewHeight * previewScale}px` : 'auto',
              overflow: 'hidden',
              margin: '0 auto'
            }}>
              <div style={{
                transform: `scale(${previewScale})`,
                transformOrigin: 'top left',
                width: '480px'
              }}>
                {cardContent}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default LockSheet;
